import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Divider,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useSelector } from "react-redux";
import { useFetch, useUpdate } from "../../hooks";
import { BASE_URL_TEST } from "../../utils/baseUrl";
import { urls } from "../../urls/urls";
import { Stepper, Step, StepLabel } from "@mui/material";
import {
  GoogleMap,
  MarkerF,
  useJsApiLoader,
  DirectionsRenderer,
} from "@react-google-maps/api";
import jsPDF from "jspdf";
import { convertTimeStampToDate, formatDateTime } from "../../utils/DateFormats";
import { ConstructionOutlined } from "@mui/icons-material";

const OrderCard = ({
  order,
  fetchOrdersData,
  customerId,
  fetchCancelOrdersData,
  handleTrackOrder,
  handleViewOrder,
  handleOpenInvoiceModal,
}) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelVendorData, setCancelVendorData] = useState(null);
  const [cancelReasonError, setCancelReasonError] = useState("");
  if (!order) return null;

  const handleCancelOrder = (vendorCartId, vendorId, orderStatus, reason) => {
    const cancelUrl = urls("cancelOrder");
    const body = {
      cart_id: vendorCartId,
      vendor_id: vendorId,
      order_status: "Cancelled",
      customer_id: customerId,
      previous_status: orderStatus,
      reason: reason,
    };
    fetchCancelOrdersData(cancelUrl, body);
  };

  const confirmCancelOrder = () => {
    if (!cancelReason.trim()) {
      setCancelReasonError("Cancel reason is required");
      return;
    }

    setCancelReasonError("");

    handleCancelOrder(
      cancelVendorData.cart_id,
      cancelVendorData.vendor_id,
      cancelVendorData.order_status,
      cancelReason,
    );

    setCancelDialogOpen(false);
    setCancelReason("");
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", xl: 1800 },
        display: "flex",
        flexDirection: "column",
        p: 0,
        m: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "#E6533C",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 2,
          p: 2,
          m: 0,
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          Order ID: {order.cart_number}
        </Typography>
      </Box>

      <CardContent>
        {order.vendors?.map((vendor) => (
          <Box key={vendor.cart_vendor_id} sx={{ mb: 4 }}>
            {/* Order Details Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
              }}
            >
              {/* Vendor Order ID */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Vendor Order ID
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#333",
                    mt: 0.5,
                    wordBreak: "break-word",
                  }}
                >
                  {vendor?.cart_number ?? "--"}
                </Typography>
              </Box>

              {/* Transaction Date */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Transaction Date & Time
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#333",
                    mt: 0.5,
                  }}
                >
                  {order?.transaction_date_time
                    ? formatDateTime(order.transaction_date_time)
                    : "--"}
                </Typography>
              </Box>

              {/* Distance */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Distance
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#333",
                    mt: 0.5,
                  }}
                >
                  {vendor?.distance ? `${vendor.distance} km` : "--"}
                </Typography>
              </Box>
            </Box>

            {vendor.items?.map((item) => (
              <Box
                key={item.cart_item_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  mb: 2.5,
                  p: 2,
                  backgroundColor: "#ffffff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  transition: "box-shadow 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  },
                }}
              >
                {/* Product Image */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 90,
                    height: 90,
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={item.product?.images?.[0]}
                    alt={item.product?.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      padding: "8px",
                    }}
                  />
                </Box>

                {/* Product Details */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      wordBreak: "break-word",
                      color: "#1a1a1a",
                      mb: 0.75,
                    }}
                  >
                    {item.product?.name}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                        }}
                      >
                        Quantity
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: "#333" }}
                      >
                        {item.quantity}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                        }}
                      >
                        Variant
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: "#333" }}
                      >
                        {item.variant_options?.length > 0
                          ? item.variant_options[0]?.option_name
                          : "--"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Price Section */}
                <Box
                  sx={{
                    flexShrink: 0,
                    textAlign: "right",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {item.price !== item.sellingPrice && (
                    <Typography
                      variant="body2"
                      sx={{
                        textDecoration: "line-through",
                        color: "#999",
                        fontWeight: 500,
                      }}
                    >
                        <span>₹</span> <span>{item.quantity * item.price}</span>
                    </Typography>
                  )}
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: "#E6533C",
                      fontSize: "1.1rem",
                    }}
                  >
                   <span>₹</span> <span>{item.quantity * item.sellingPrice}</span>
                  </Typography>
                </Box>
              </Box>
            ))}

            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="flex-start"
              gap={2}
              mt={3}
              pt={2}
              borderTop="1px solid #e0e0e0"
            >
              <Button
                variant="contained"
                startIcon={<LocationOnIcon />}
                sx={{
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  backgroundColor: "#E6533C",
                  "&:hover": {
                    backgroundColor: "#D14730",
                  },
                }}
                onClick={() => {
                  handleTrackOrder(vendor.order_status?.toLowerCase().trim());
                }}
              >
                Track Order
              </Button>

              {vendor.order_status?.toLowerCase().trim() === "pending" && (
                <>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: "6px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.5,
                      py: 1,
                      color: "#d32f2f",
                      borderColor: "#d32f2f",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                        borderColor: "#d32f2f",
                      },
                    }}
                    onClick={() => {
                      setCancelVendorData({
                        cart_id: vendor.cart_id,
                        vendor_id: vendor.vendor_id,
                        order_status: vendor.order_status,
                      });
                      setCancelDialogOpen(true);
                    }}
                  >
                    Cancel Order
                  </Button>

                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: "6px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.5,
                      py: 1,
                      borderColor: "#1976d2",
                      color: "#1976d2",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                        borderColor: "#1976d2",
                      },
                    }}
                    onClick={() => handleViewOrder(vendor)}
                  >
                    View Order
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: "6px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.5,
                      py: 1,
                      color: "#00897B",
                      borderColor: "#00897B",
                      "&:hover": {
                        backgroundColor: "rgba(0, 137, 123, 0.04)",
                        borderColor: "#00897B",
                      },
                    }}
                    onClick={() => handleOpenInvoiceModal(vendor)}
                  >
                    Download Invoice
                  </Button>
                </>
              )}
            </Box>
          </Box>
        ))}
      </CardContent>
      {/* Cancel Reason Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Cancel Order</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            label="Reason for cancellation"
            multiline
            rows={4}
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              setCancelReasonError("");
            }}
            error={!!cancelReasonError}
            helperText={cancelReasonError}
            sx={{ mt: 1 }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>

          <Button
            onClick={confirmCancelOrder}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Divider sx={{ my: 2 }} />
    </Box>
  );
};

const OnTheWay = () => {
  const customerId = useSelector((state) => state?.user?.customerId);
  const apptoken = useSelector((state) => state?.user?.apptoken);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [pendingOrdersList, setPendingOrdersList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [trackOderModal, setTrackOderModal] = useState(false);
  const [viewOrderData, setViewOrderData] = useState(null);
  const [isViewOrderModalOpen, setViewOrderModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [data, setData] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);
  const [snackbarState, setSnackbarState] = useState({
    message: "",
    status: "",
  });

  const {
    data: orderData,
    error: orderError,
    fetchData: fetchOrdersData,
  } = useFetch(BASE_URL_TEST + urls("getOrderByUserId", customerId), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });

  const {
    data: cancelOrdersData,
    error: cancelOrdersError,
    updateData: fetchCancelOrdersData,
  } = useUpdate(BASE_URL_TEST, {
    method: "PUT",
    headers: {
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });
  const {
    data: siteSettingsData,
    isLoading: isSiteSettingsDataLoading,
    error: siteSettingsError,
    fetchData: fetchSiteSettingsData,
  } = useFetch(BASE_URL_TEST + urls("getAdminSiteSettings"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });

  useEffect(() => {
    if (customerId) {
      fetchOrdersData();
      fetchSiteSettingsData();
    }
  }, [customerId]);

  useEffect(() => {
    if (orderError) {
      setErrorMessage("Failed to fetch orders.");
      setPendingOrdersList([]);
    } else if (orderData) {
      if (orderData.status === true) {
        const filteredOrders = orderData.data
          .filter((order) =>
            order.vendors?.some(
              (vendor) =>
                typeof vendor.order_status === "string" &&
                vendor.order_status.trim().toLowerCase() === "pending",
            ),
          )
          .sort(
            (a, b) =>
              new Date(b.transaction_date_time) -
              new Date(a.transaction_date_time),
          );
        setPendingOrdersList(filteredOrders);
        setErrorMessage("");
      } else {
        setPendingOrdersList([]);
        setErrorMessage(orderData.message || "No orders found.");
      }
    }
  }, [orderData, orderError]);

  useEffect(() => {
    if (cancelOrdersError) {
      console.error("Cancel order error:", cancelOrdersError);
    } else if (cancelOrdersData) {
      setSnackbarState({
        message: cancelOrdersData["message"],
        status: cancelOrdersData["status"],
      });
      setModalOpen(true);
      fetchOrdersData();
    }
  }, [cancelOrdersData, cancelOrdersError]);

  useEffect(() => {
    if (siteSettingsError) {
    } else if (siteSettingsData && siteSettingsData["status"] === true) {
      setData(siteSettingsData["data"]);
    }
  }, [siteSettingsData, siteSettingsError]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSnackbarState({ message: "", status: "" });
  };

  const handleTrackOrder = (orderStatus) => {
    setTrackingOrder({ order_status: orderStatus });
    setTrackOderModal(true);
  };

  const getActiveStep = (status) => {
    const statusMap = {
      pending: 0,
      confirmed: 1,
      processing: 2,
      picked: 3,
      shipped: 4,
      delivered: 5,
    };
    return statusMap[status?.toLowerCase()?.trim()] ?? 0;
  };

  const handleViewOrder = (vendor) => {
    setViewOrderData(vendor);
    setViewOrderModalOpen(true);
    setMapModalOpen(true);
  };

  const customerLat = useSelector(
    (state) => state.user?.userLocation?.latitude ?? null,
  );
  const customerLng = useSelector(
    (state) => state.user?.userLocation?.longitude ?? null,
  );

  const origin =
    customerLat && customerLng
      ? { lat: parseFloat(customerLat), lng: parseFloat(customerLng) }
      : null;

  const destination =
    viewOrderData?.vendorDetails?.latitude &&
    viewOrderData?.vendorDetails?.longitude
      ? {
          lat: parseFloat(viewOrderData.vendorDetails.latitude),
          lng: parseFloat(viewOrderData.vendorDetails.longitude),
        }
      : null;

  const [directions, setDirections] = useState(null);

  useEffect(() => {
    if (!origin || !destination || !window.google) return;
    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          console.error("Directions error:", status);
        }
      },
    );
  }, [origin, destination]);

  const pathCoordinates = useMemo(() => {
    if (origin && destination) {
      return [destination, origin];
    }
    return [];
  }, [origin, destination]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyA6CszWLFAJsbbDNtAtoXEWgWzqhU3pHB8",
  });

  const mapRef = useRef(null);

  const onLoad = (map) => {
    mapRef.current = map;
  };
  // Handle opening invoice modal
  const handleOpenInvoiceModal = (vendor) => {
    setInvoiceData(vendor);
    setInvoiceModalOpen(true);
  };

  // PDF Generation Function
  const generateInvoicePDF = () => {
    if (!invoiceData) return;

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;

      // Calculate totals
      let subtotal = 0;
      invoiceData.items?.forEach((item) => {
        subtotal += parseFloat(item.sellingPrice) * item.quantity;
      });
      //const shipping = 50.0;
      const discount = 0.0;
      const platformFee = Number(data?.platform_fee || 0);
      const deliveryFee = Number(data?.delivery_partner_fee || 0);
      const gstPercent = Number(data?.gst_charge || 0);
      const gstAmount = (subtotal * gstPercent) / 100;

      const total = subtotal + platformFee + deliveryFee + gstAmount - discount;

      const currentDate = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", pageWidth / 2, 20, { align: "center" });

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Sthanick Web Services", pageWidth / 2, 30, { align: "center" });

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text("Payment Invoice", pageWidth / 2, 38, { align: "center" });

      // Add order details
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Order ID: #${invoiceData.cart_number}`, 20, 50);
      doc.text(`Date: ${currentDate}`, 20, 58);
      doc.text(`Total Items: ${invoiceData.items?.length || 0}`, 20, 66);

      doc.setLineWidth(0.5);
      doc.line(20, 75, pageWidth - 20, 75);

      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Order Items", 20, 85);

      // Table headers
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      let yPos = 95;
      doc.text("SKU", 20, yPos);
      doc.text("NAME", 40, yPos);
      doc.text("PRICE", 110, yPos);
      doc.text("QTY", 140, yPos);
      doc.text("DISC.", 160, yPos);
      doc.text("TOTAL", 180, yPos);

      doc.setLineWidth(0.3);
      doc.line(20, yPos + 2, pageWidth - 20, yPos + 2);

      // Add items
      doc.setFont("helvetica", "normal");
      yPos += 10;

      invoiceData.items?.forEach((item, index) => {
        const itemTotal = parseFloat(item.sellingPrice) * item.quantity;

        doc.text(`${index + 1}`, 20, yPos);

        // Wrap long product names
        const productName = item.product?.name || "N/A";
        if (productName.length > 25) {
          doc.text(productName.substring(0, 25) + "...", 40, yPos);
        } else {
          doc.text(productName, 40, yPos);
        }

        doc.text(`₹${item.sellingPrice}`, 110, yPos);
        doc.text(`x${item.quantity}`, 140, yPos);
        doc.text("0%", 160, yPos);
        doc.text(`₹${itemTotal.toFixed(2)}`, 180, yPos);

        yPos += 10;
      });

      yPos += 5;
      doc.line(20, yPos, pageWidth - 20, yPos);

      // Payment Summary
      yPos += 15;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Payment Summary", 20, yPos);

      yPos += 12;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      doc.text("Subtotal:", 20, yPos);
      doc.text(`₹${subtotal.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      // yPos += 8;
      // doc.text("Shipping:", 20, yPos);
      // doc.text(`₹${shipping.toFixed(2)}`, pageWidth - 40, yPos, {
      //   align: "right",
      // });
      yPos += 8;
      doc.text("Platform Fee:", 20, yPos);
      doc.text(`₹${platformFee.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      yPos += 8;
      doc.text("Delivery Partner Fee:", 20, yPos);
      doc.text(`₹${deliveryFee.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      yPos += 8;

      doc.text(`GST (${gstPercent}%):`, 20, yPos);
      doc.text(`₹${gstAmount.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });
      yPos += 8;
      doc.text("Discount:", 20, yPos);
      doc.setTextColor(255, 0, 0);
      doc.text(`-₹${discount.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });

      doc.setTextColor(0, 0, 0);

      // Total line
      yPos += 12;
      doc.setLineWidth(0.5);
      doc.line(20, yPos - 4, pageWidth - 20, yPos - 4);

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Total:", 20, yPos);
      doc.setTextColor(25, 118, 210);
      doc.text(`₹${total.toFixed(2)}`, pageWidth - 40, yPos, {
        align: "right",
      });
      doc.setTextColor(0, 0, 0);

      // Vendor Information
      yPos += 20;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("Vendor Information", 20, yPos);

      yPos += 10;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      doc.text(`Vendor ID: ${invoiceData.vendor_id}`, 20, yPos);
      yPos += 7;
      doc.text(
        `Shop Name: ${invoiceData.vendorDetails?.shop_name || "N/A"}`,
        20,
        yPos,
      );
      yPos += 7;
      doc.text(
        `Phone: ${invoiceData.vendorDetails?.phone_number || "N/A"}`,
        20,
        yPos,
      );
      yPos += 7;
      doc.text(`Email: ${invoiceData.vendorDetails?.email || "N/A"}`, 20, yPos);
      yPos += 7;
      doc.text(
        `Location: ${invoiceData.vendorDetails?.location || "N/A"}`,
        20,
        yPos,
      );
      yPos += 7;
      doc.text(`State: ${invoiceData.vendorDetails?.state || "N/A"}`, 20, yPos);
      yPos += 7;
      doc.text(
        `Country: ${invoiceData.vendorDetails?.country || "N/A"}`,
        20,
        yPos,
      );

      // Add footer
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("Thank you for your business!", pageWidth / 2, 280, {
        align: "center",
      });

      // Save the PDF
      doc.save(
        `Invoice_${invoiceData.cart_number}_${
          new Date().toISOString().split("T")[0]
        }.pdf`,
      );

      // Close modal after download
      setInvoiceModalOpen(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };
  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        On the Way
      </Typography>
      <Card>
        <Box display="flex" flexDirection="column" gap={5} p={2}>
          {errorMessage && (
            <Typography color="error">{errorMessage}</Typography>
          )}
          {pendingOrdersList.length === 0 && !errorMessage && (
            <Typography>No pending orders found.</Typography>
          )}

          {pendingOrdersList.map((order) => (
            <OrderCard
              key={order.cart_id}
              order={order}
              customerId={customerId}
              fetchOrdersData={fetchOrdersData}
              fetchCancelOrdersData={fetchCancelOrdersData}
              handleTrackOrder={handleTrackOrder}
              handleViewOrder={handleViewOrder}
              handleOpenInvoiceModal={handleOpenInvoiceModal}
            />
          ))}
        </Box>
      </Card>

      {/* Snackbar Modal */}
      <Dialog open={isModalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>
          {snackbarState.status === true ? "Success" : "Error"}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{ color: snackbarState.status === true ? "green" : "red" }}
          >
            {snackbarState.message}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloseModal}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Track Order Modal */}
      <Dialog
        open={trackOderModal}
        onClose={() => setTrackOderModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Track Order</DialogTitle>
        <DialogContent>
          <Stepper
            activeStep={getActiveStep(trackingOrder?.order_status)}
            alternativeLabel
          >
            {["Pending", "Confirmed", "Delivered"].map((label, index) => (
              <Step key={label}>
                <StepLabel
                  optional={
                    index === getActiveStep(trackingOrder?.order_status) ? (
                      <Typography variant="caption">Current</Typography>
                    ) : null
                  }
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTrackOderModal(false)}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Order Modal */}
      <Dialog
        open={isViewOrderModalOpen}
        onClose={() => setViewOrderModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Order & Vendor Details</DialogTitle>
        <DialogContent dividers>
          {viewOrderData && (
            <>
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Order Info
                </Typography>
                <Typography>Order ID: {viewOrderData.cart_number}</Typography>
                <Typography>Status: {viewOrderData.order_status}</Typography>
                <Typography>
                  Distance: {viewOrderData.distance ?? "--"}
                </Typography>
                <Typography>
                  Items: {viewOrderData.items?.length ?? 0}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Ordered Items
                </Typography>
                {viewOrderData.items?.map((item) => (
                  <Box key={item.cart_item_id} mb={2}>
                    <Typography fontWeight="bold">
                      {item.product?.name}
                    </Typography>
                    <Typography>Qty: {item.quantity}</Typography>
                    <Typography>Price: ₹{item.sellingPrice}</Typography>
                  </Box>
                ))}
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Vendor Info
                </Typography>
                <Typography>Vendor ID: {viewOrderData.vendor_id}</Typography>
                <Typography>
                  Name: {viewOrderData.vendorDetails?.shop_name ?? "N/A"}
                </Typography>
                <Typography>
                  Phone: {viewOrderData.vendorDetails?.phone_number ?? "N/A"}
                </Typography>
                <Typography>
                  Email: {viewOrderData.vendorDetails?.email ?? "N/A"}
                </Typography>
                <Typography>
                  Location: {viewOrderData.vendorDetails?.location ?? "N/A"}
                </Typography>
                <Typography>
                  State: {viewOrderData.vendorDetails?.state ?? "N/A"}
                </Typography>
                <Typography>
                  Country: {viewOrderData.vendorDetails?.country ?? "N/A"}
                </Typography>
              </Box>

              {/* Map Section */}
              {isLoaded && pathCoordinates.length === 2 && (
                <Box
                  sx={{
                    height: "300px",
                    width: "100%",
                    borderRadius: 2,
                    overflow: "hidden",
                    mb: 2,
                  }}
                >
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    // center={{
                    //   lat: (pathCoordinates[0].lat + pathCoordinates[1].lat) / 2,
                    //   lng: (pathCoordinates[0].lng + pathCoordinates[1].lng) / 2,
                    // }}
                    zoom={13}
                    onLoad={onLoad}
                  >
                    {directions && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{
                          polylineOptions: {
                            strokeColor: "#FF0000",
                            strokeWeight: 4,
                          },
                        }}
                      />
                    )}
                    <MarkerF position={pathCoordinates[0]} />
                    <MarkerF position={pathCoordinates[1]} />
                  </GoogleMap>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewOrderModalOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Invoice Modal */}
      <Dialog
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem" }}
        >
          Invoice Details
        </DialogTitle>
        <DialogContent dividers>
          {invoiceData && (
            <>
              <Box mb={3}>
                <Typography variant="body1" gutterBottom>
                  <strong>Order ID:</strong> #{invoiceData.cart_number}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Total Items:</strong> {invoiceData.items?.length || 0}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Date:</strong>{" "}
                  {new Date().toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Order Items
              </Typography>

              <Box sx={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "10px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        SKU
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        NAME
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        PRICE
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        QTY
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        DISC.
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        TOTAL
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items?.map((item, index) => (
                      <tr key={item.cart_item_id}>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          {item.product?.name}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          ₹{item.sellingPrice}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          x{item.quantity}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                            color: "red",
                          }}
                        >
                          0%
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          ₹
                          {(
                            parseFloat(item.sellingPrice) * item.quantity
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Payment Summary
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Subtotal:</Typography>
                  <Typography>
                    ₹
                    {invoiceData.items
                      ?.reduce(
                        (sum, item) =>
                          sum + parseFloat(item.sellingPrice) * item.quantity,
                        0,
                      )
                      .toFixed(2)}
                  </Typography>
                </Box>
                {/* <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Shipping:</Typography>
                  <Typography>₹50.00</Typography>
                </Box> */}
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Platform Fee:</Typography>
                  <Typography>
                    ₹{Number(data?.platform_fee || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Delivery Partner Fee:</Typography>
                  <Typography>
                    ₹{Number(data?.delivery_partner_fee || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>
                    GST Charge({Number(data?.gst_charge || 0)}%):
                  </Typography>
                  <Typography>
                    {/* ₹{Number(data?.gst_charge || 0).toFixed(2)} */}₹
                    {(
                      ((invoiceData.items?.reduce(
                        (sum, item) =>
                          sum + Number(item.sellingPrice) * item.quantity,
                        0,
                      ) || 0) *
                        Number(data?.gst_charge || 0)) /
                      100
                    ).toFixed(2)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Discount:</Typography>
                  <Typography sx={{ color: "red" }}>-₹0.00</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight="bold">
                    Total:
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#1976d2" }}
                  >
                    {/* {(
                      invoiceData.items?.reduce(
                        (sum, item) =>
                          sum + parseFloat(item.sellingPrice) * item.quantity,
                        0,
                      ) + 50
                    ).toFixed(2)} */}
                    ₹
                    {(
                      (invoiceData.items?.reduce(
                        (sum, item) =>
                          sum + Number(item.sellingPrice) * item.quantity,
                        0,
                      ) || 0) +
                      Number(data?.platform_fee || 0) +
                      Number(data?.delivery_partner_fee || 0) +
                      ((invoiceData.items?.reduce(
                        (sum, item) =>
                          sum + Number(item.sellingPrice) * item.quantity,
                        0,
                      ) || 0) *
                        Number(data?.gst_charge || 0)) /
                        100
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, pt: 2 }}>
          <Button
            onClick={generateInvoicePDF}
            variant="contained"
            sx={{
              backgroundColor: "#00897B",
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              "&:hover": {
                backgroundColor: "#00796B",
              },
            }}
          >
            Download Invoice
          </Button>
          <Button
            onClick={() => setInvoiceModalOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 4,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnTheWay;
